import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import { definePreset } from '@primevue/themes';

import Tooltip from 'primevue/tooltip';
// THEMES
import Aura from '@primevue/themes/aura';

import type { Preset } from '@primeuix/themes/types';
import type { App, Plugin } from 'vue';

interface CreatePrimeVuePluginOptions {
  preset?: Preset;
}

export const createPrimeVuePlugin = ({
  preset = BasePrimeVuePreset,
}: CreatePrimeVuePluginOptions = {}): Plugin => ({
  install(app: App) {
    app.use(PrimeVue, {
      ripple: true,
      zIndex: {
        modal: 1100,
        overlay: 1000,
        menu: 1000,
        tooltip: 11000,
      },
      theme: {
        preset,
        options: {
          darkModeSelector: false, // TODO: Make dark mode work,
          cssLayer: true,
        },
      },
    });
    app.use(ToastService);
    // Components auto-import through @primevue/auto-import-resolver; directive
    // resolution is off in the Vite config, so the tooltip stays registered here.
    app.directive('tooltip', Tooltip);
  },
});

// Values dereference the Figma token layer in libs/ui/src/styles/theme-vars.scss.
// Do not reintroduce literal hex values here.

// Bebawa_NewDesignSystem draws InputText, Select and MultiSelect as one field:
// the AutoComplete spec (Figma 518:49492) embeds the InputText component itself.
const FORM_FIELD_SURFACE = {
  background: 'var(--color-input-bg)',
  disabledBackground: 'var(--color-input-disabled)',
  filledBackground: 'var(--color-input-fill)',
  filledHoverBackground: 'var(--color-input-fill)',
  filledFocusBackground: 'var(--color-input-fill)',
  borderColor: 'var(--color-border-idle)',
  hoverBorderColor: 'var(--color-input-border-hover)',
  focusBorderColor: 'var(--color-border-active)',
  invalidBorderColor: 'var(--color-button-danger-idle)',
  color: 'var(--color-text-primary)',
  disabledColor: 'var(--color-text-disabled)',
  placeholderColor: 'var(--color-text-placeholder)',
  invalidPlaceholderColor: 'var(--color-state-danger)',
  borderRadius: 'var(--radius-12)',
  paddingX: 'var(--spacing-sm)',
  paddingY: '0.5rem',
  sm: {
    fontSize: 'var(--font-size-sm)',
    paddingX: 'var(--spacing-sm)',
    // 16px line box + 2x7px + 2x1px border = the 32px "S", as on Button.
    paddingY: '0.4375rem',
  },
  lg: {
    fontSize: 'var(--font-size-md)',
    paddingX: 'var(--spacing-sm)',
    paddingY: 'var(--spacing-sm)',
  },
} as const;

// The shared overlay + SelectOption rows (Figma 518:49347 / _select-option).
const SELECT_OVERLAY = {
  background: 'var(--color-bg-elevated)',
  borderColor: 'var(--color-border-idle)',
  borderRadius: 'var(--radius-12)',
  color: 'var(--color-text-primary)',
  // Shadow/Context Menu
  shadow: '0 4px 6px -1px rgb(0 0 0 / 10%)',
} as const;

const SELECT_LIST = {
  padding: 'var(--spacing-xs)',
  gap: '2px',
} as const;

// Figma SelectOption (518:49284): 8px gutter between the leading glyph and label.
// Only MultiSelect exposes `option.gap` as a token; Select needs the css hatch.
const SELECT_OPTION = {
  padding: '6px var(--spacing-s)',
  borderRadius: 'var(--radius-12)',
  gap: 'var(--spacing-s)',
  color: 'var(--color-text-primary)',
  focusColor: 'var(--color-text-primary)',
  focusBackground: 'var(--color-menu-item-hover-bg)',
  selectedColor: 'var(--color-text-primary)',
  // Figma binds SelectOption/selected to Table/TableRowSelectedBg. The menu
  // token it used to read has no dark value, so the light highlight survived
  // into dark mode and buried the label.
  selectedBackground: 'var(--color-table-row-selected-bg)',
  selectedFocusColor: 'var(--color-text-primary)',
  selectedFocusBackground: 'var(--color-table-row-selected-bg)',
} as const;

// Figma draws the chevron zone as a square button matching the control height.
const SELECT_DROPDOWN = {
  width: '2.25rem',
  color: 'var(--color-icon-muted)',
} as const;

// Bebawa_NewDesignSystem / Button, Ghost fill (Figma 352:18246). PrimeVue calls
// the fill `text`, and resolves it per severity.
const GHOST_FILL = {
  hoverBackground: 'var(--color-button-ghost-hover)',
  activeBackground: 'var(--color-button-ghost-active)',
} as const;

// Bebawa_NewDesignSystem / Button, Secondary fill (Figma 352:18246). Aura fills
// it from the surface ramp, which lands a step below the Figma value and drops
// the label to the secondary text colour.
const SECONDARY_FILL = {
  background: 'var(--color-button-secondary-idle)',
  hoverBackground: 'var(--color-button-secondary-hover)',
  activeBackground: 'var(--color-button-secondary-active)',
  borderColor: 'var(--color-button-secondary-idle)',
  hoverBorderColor: 'var(--color-button-secondary-hover)',
  activeBorderColor: 'var(--color-button-secondary-active)',
  color: 'var(--color-text-primary)',
  hoverColor: 'var(--color-text-primary)',
  activeColor: 'var(--color-text-primary)',
} as const;

// Bebawa_NewDesignSystem / Button, Outlined fill (Figma 352:18246). Figma draws
// one Outlined Button/Border for every colour it pairs with outlined; Aura
// tints each pairing from its own ramp instead (secondary a step too faint,
// contrast near-black in light and near-white in dark).
const OUTLINED_FILL = {
  borderColor: 'var(--color-button-outlined-border)',
  color: 'var(--color-text-primary)',
  hoverBackground: 'var(--color-button-outlined-hover)',
  activeBackground: 'var(--color-button-outlined-active)',
} as const;

// The month and year pickers in the DatePicker header share one button shape.
const DATEPICKER_TITLE_BUTTON = {
  padding: '0.21875rem 0.4375rem',
  borderRadius: 'var(--radius-12)',
  color: 'var(--color-text-primary)',
  hoverBackground: 'var(--color-button-ghost-hover)',
  hoverColor: 'var(--color-text-primary)',
} as const;

export const BasePrimeVuePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: 'var(--color-brand-50)',
      100: 'var(--color-brand-100)',
      200: 'var(--color-brand-200)',
      300: 'var(--color-brand-300)',
      400: 'var(--color-brand-400)',
      500: 'var(--color-brand-500)',
      600: 'var(--color-brand-600)',
      700: 'var(--color-brand-700)',
      800: 'var(--color-brand-800)',
      900: 'var(--color-brand-900)',
      950: 'var(--color-brand-950)',
    },
    secondary: {
      50: '{zinc.50}',
      100: '{zinc.100}',
      200: '{zinc.200}',
      300: '{zinc.300}',
      400: '{zinc.400}',
      500: '{zinc.500}',
      600: '{zinc.600}',
      700: '{zinc.700}',
      800: '{zinc.800}',
      900: '{zinc.900}',
      950: '{zinc.950}',
    },
    borderRadius: {
      m: 'var(--radius-8)',
      lg: 'var(--radius-12)',
      xl: 'var(--radius-16)',
      xxl: 'var(--radius-24)',
      xxxl: 'var(--radius-24)',
      absolut: 'var(--radius-999)',
    },
    colorScheme: {
      light: {
        primary: {
          // Figma's dark brand is lighter (#66a5ff); white-on-it is only 2.5:1.
          // text-inverse is near-white in light and near-black in dark.
          contrastColor: 'var(--color-text-inverse)',
        },
        surface: {
          0: 'var(--color-bg-widget)',
          50: 'var(--color-bg-canvas)',
          100: 'var(--color-bg-surface)',
          200: 'var(--color-border-idle)',
          300: 'var(--color-border-strong)',
          400: 'var(--color-input-border-hover)',
          500: 'var(--color-text-muted)',
          600: 'var(--color-text-secondary)',
          700: 'var(--color-text-primary)',
          800: 'var(--color-base-surface-300)',
          900: 'var(--color-button-contrast-hover)',
          950: 'var(--color-button-contrast-idle)',
        },
      },
    },
  },
  components: {
    selectbutton: {
      root: { borderRadius: 'var(--p-border-radius-xxl)' },
    },
    // Figma draws the S toggle at 32px, but stacks two paddings to get there
    // (root 3px + content 4px around a 16px line box), and 3px has no token.
    togglebutton: {
      css: `
        .p-togglebutton-label {
          line-height: var(--line-height-xs);
        }

        .p-togglebutton-sm {
          height: 2rem;
        }
      `,
      root: { borderRadius: 'var(--p-border-radius-xxl)' },
      content: { borderRadius: 'var(--p-border-radius-xxl)' },
    },
    menubar: {
      colorScheme: {
        baseItemBorderRadius: 'var(--p-border-radius-xxl)',
        root: {
          borderRadius: '14px',
        },
      },
    },
    card: {
      colorScheme: {
        root: {
          // TODO(redesign): no counterpart in the new Figma palette.
          background: '#25242B',
          borderRadius: 'var(--p-border-radius-xxl)',
          color: 'white',
        },
        subtitleColor: 'white',
        captionGap: '3rem',
        bodyGap: '2rem',
        bodyPadding: '2.5rem',
      },
    },
    // Bebawa_NewDesignSystem / Tag (Figma 366:36597). Severity colours stay in
    // base.scss, which maps --p-tag-* onto the --color-tag-* palette.
    tag: {
      // Tag has no line-height token, so height would follow the font metrics.
      // Pinning it makes 4 + 16 + 4 the 24px the design specifies.
      css: `
        .p-tag {
          line-height: var(--line-height-xs);
        }
      `,
      root: {
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        padding: 'var(--spacing-xs) var(--spacing-s)',
        gap: 'var(--spacing-xs)',
        borderRadius: 'var(--radius-12)',
      },
      icon: {
        size: 'var(--icon-size-xs)',
      },
    },
    // Bebawa_NewDesignSystem / Skeleton (Figma 1276:18226). `shape="circle"`
    // keeps PrimeVue's own 50%, so only the rectangular radius is set here.
    skeleton: {
      root: {
        background: 'var(--color-bg-skeleton)',
        borderRadius: 'var(--radius-12)',
      },
    },
    // Bebawa_NewDesignSystem / ToggleSwitch (Figma 2613:159620).
    toggleswitch: {
      css: `
        .p-toggleswitch.p-toggleswitch-checked.p-disabled .p-toggleswitch-slider {
          background: var(--color-brand-300);
        }

        .p-toggleswitch.p-toggleswitch-checked.p-disabled .p-toggleswitch-handle {
          background: var(--color-base-white);
        }
      `,
      root: {
        width: '2.1875rem',
        height: '1.3125rem',
        gap: '0.21875rem',
        borderRadius: 'var(--radius-999)',
      },
      handle: {
        size: '0.875rem',
        borderRadius: 'var(--radius-999)',
      },
      colorScheme: {
        light: {
          root: {
            background: 'var(--color-button-secondary-idle)',
            hoverBackground: 'var(--color-button-secondary-hover)',
            checkedBackground: 'var(--color-brand-400)',
            checkedHoverBackground: 'var(--color-brand-500)',
          },
          handle: {
            background: 'var(--color-base-white)',
            hoverBackground: 'var(--color-base-white)',
            checkedBackground: 'var(--color-base-white)',
            checkedHoverBackground: 'var(--color-base-white)',
          },
        },
      },
    },
    // Bebawa_NewDesignSystem / Tooltip (Figma 512:31316). Text size is inherited
    // from `body`, which already matches the 14px in the design.
    tooltip: {
      root: {
        background: 'var(--color-base-contrast)',
        color: 'var(--color-text-inverse)',
        borderRadius: 'var(--radius-8)',
        // 0.875x of the 8/12px step; the Figma scale has no token at this size.
        padding: '0.4375rem 0.65625rem',
        // Drives the arrow too: border-width `gutter` renders it 5x10px.
        gutter: '0.3125rem',
        shadow: '0 4px 6px -1px rgb(0 0 0 / 10%), 0 2px 4px -2px rgb(0 0 0 / 10%)',
      },
    },
    // Bebawa_NewDesignSystem / Button (Figma 352:18246). PrimeVue hardcodes
    // `.p-button { font-size: 1rem }` and gives the base icon no size token,
    // so both need the css escape hatch.
    button: {
      css: `
        .p-button {
          font-size: var(--font-size-sm);
          line-height: var(--line-height-xs);
        }

        .p-button:not(.p-button-sm):not(.p-button-lg) .p-button-icon {
          font-size: var(--icon-size-sm);
        }
      `,
      root: {
        gap: 'var(--spacing-s)',
        paddingX: 'var(--spacing-sm)',
        // 16px line-height + 2x10px padding + 2x1px border = the 38px "M".
        paddingY: '0.625rem',
        iconOnlyWidth: '2.375rem',
        sm: {
          fontSize: 'var(--font-size-sm)',
          paddingX: 'var(--spacing-sm)',
          // ...and 2x7px for the 32px "S".
          paddingY: '0.4375rem',
          iconOnlyWidth: '2rem',
        },
        label: {
          fontWeight: 'var(--font-weight-medium)',
        },
      },
      // Only the light scheme is ever emitted - `data-theme` swaps the value
      // behind each `--color-*`, so one entry here serves both modes. Anything
      // placed on `colorScheme.root` instead is dropped and Aura's own
      // `colorScheme.light` wins.
      colorScheme: {
        light: {
          root: {
            primary: {
              background: 'var(--color-button-primary-idle)',
            },
            secondary: SECONDARY_FILL,
          },
          outlined: {
            secondary: OUTLINED_FILL,
            contrast: OUTLINED_FILL,
          },
          // Figma draws one Button/Ghost/Hover fill for every colour it pairs
          // with ghost; Aura instead tints each severity with its own ramp.
          // Danger keeps Aura's red so a destructive ghost still reads as one.
          text: {
            primary: GHOST_FILL,
            secondary: GHOST_FILL,
            contrast: GHOST_FILL,
          },
        },
      },
    },
    // Bebawa_NewDesignSystem treetable (Figma 2145:168579), which also rules the
    // grid with Table/Divider where Aura reaches for the form border. Semibold
    // headers over that hairline, and 48px rows in the secondary text colour so
    // the value columns read against them. The header keeps its bg-widget fill:
    // it already matches the card behind every table, and a transparent one
    // would show rows through the sticky header on scrollable tables.
    datatable: {
      css: `
        .p-datatable-thead > tr > th {
          font-size: var(--font-size-sm);
          line-height: var(--line-height-sm);
        }

        .p-datatable-tbody > tr > td {
          height: 3rem;
          color: var(--color-text-secondary);
          font-size: var(--font-size-sm);
          font-weight: var(--font-weight-medium);
          line-height: var(--line-height-sm);
        }
      `,
      // 9px bottom: the design's optical baseline, with no token at that step.
      headerCell: { padding: 'var(--spacing-s) var(--spacing-sm) 9px' },
      bodyCell: { padding: 'var(--spacing-s) var(--spacing-sm) 9px' },
      columnTitle: { fontWeight: 'var(--font-weight-semibold)' },
      sortIcon: {
        size: 'var(--icon-size-sm)',
        color: 'var(--color-icon-muted)',
        hoverColor: 'var(--color-text-primary)',
      },
      colorScheme: {
        light: {
          headerCell: { borderColor: 'var(--color-table-divider)' },
          bodyCell: { borderColor: 'var(--color-table-divider)' },
        },
      },
    },
    // Bebawa_NewDesignSystem / Checkbox (Figma 2611:236260). Aura's 20px box is
    // a size larger than the design, in the table and in the select overlays.
    // The 17.5px/#cbd5e1 checkbox belongs to the pre-redesign frame.
    checkbox: {
      root: {
        width: '1rem',
        height: '1rem',
        borderColor: 'var(--color-button-outlined-border)',
      },
      // Figma draws the check at 12.25px inside the 16px box.
      icon: { size: '0.765625rem' },
    },
    listbox: {
      colorScheme: {
        optionBorderRadius: 'var(--p-border-radius-xxl)',
      },
    },
    // Bebawa_NewDesignSystem / InputText (Figma 454:51286). Every .p-inputtext
    // follows, which includes the inner field of Password, InputNumber,
    // InputMask and AutoComplete. Textarea keeps its own tokens.
    inputtext: {
      css: `
        .p-inputtext {
          font-size: var(--font-size-sm);
          font-weight: var(--font-weight-regular);
          line-height: var(--line-height-xs);
        }
      `,
      root: FORM_FIELD_SURFACE,
    },
    iconfield: {
      icon: {
        color: 'var(--color-icon-muted)',
      },
    },
    // Bebawa_NewDesignSystem / AutoComplete (Figma 518:49492), which is where
    // this system models the select control and its shared SelectOption rows.
    select: {
      css: `
        .p-select-label {
          font-size: var(--font-size-sm);
          font-weight: var(--font-weight-regular);
          line-height: var(--line-height-xs);
        }

        .p-select-dropdown {
          border-inline-start: 1px solid var(--color-border-idle);
          border-start-end-radius: var(--radius-12);
          border-end-end-radius: var(--radius-12);
        }

        .p-select-option {
          gap: var(--spacing-s);
        }
      `,
      root: FORM_FIELD_SURFACE,
      dropdown: SELECT_DROPDOWN,
      overlay: SELECT_OVERLAY,
      list: SELECT_LIST,
      option: SELECT_OPTION,
    },
    // Bebawa_NewDesignSystem / Textarea (Figma 512:43903) — the same field
    // surface as InputText, only taller.
    textarea: {
      css: `
        .p-textarea {
          font-size: var(--font-size-sm);
          font-weight: var(--font-weight-regular);
        }
      `,
      root: FORM_FIELD_SURFACE,
    },
    // Bebawa_NewDesignSystem / DatePicker (Figma 697:404486). The field itself
    // is a PrimeVue InputText, so it already follows the inputtext block; only
    // the panel is described here.
    datepicker: {
      css: `
        .p-datepicker-day {
          font-size: var(--font-size-md);
        }

        .p-datepicker-today > .p-datepicker-day:not(.p-datepicker-day-selected) {
          border: 1px solid var(--color-border-active);
        }
      `,
      panel: {
        background: 'var(--color-bg-elevated)',
        borderColor: 'var(--color-border-idle)',
        color: 'var(--color-text-primary)',
        borderRadius: 'var(--radius-12)',
        // Shadow/Context Menu, same as the select overlay.
        shadow: '0 4px 6px -1px rgb(0 0 0 / 10%)',
        padding: 'var(--spacing-sm)',
      },
      header: {
        background: 'transparent',
        borderColor: 'var(--color-table-divider)',
        color: 'var(--color-text-primary)',
        padding: '0 0 var(--spacing-s) 0',
      },
      title: {
        gap: '0.4375rem',
        fontWeight: 'var(--font-weight-regular)',
      },
      selectMonth: DATEPICKER_TITLE_BUTTON,
      selectYear: DATEPICKER_TITLE_BUTTON,
      weekDay: {
        padding: '0.21875rem',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--color-text-primary)',
      },
      date: {
        width: '1.75rem',
        height: '1.75rem',
        padding: '0.21875rem',
        borderRadius: 'var(--radius-999)',
        color: 'var(--color-text-primary)',
        hoverBackground: 'var(--color-table-row-hover-bg)',
        hoverColor: 'var(--color-text-primary)',
        selectedBackground: 'var(--color-brand-400)',
        selectedColor: 'var(--color-text-inverse)',
      },
      buttonbar: {
        padding: 'var(--spacing-s) 0 0 0',
        // Figma draws no divider above the Clear / Today bar.
        borderColor: 'transparent',
      },
      colorScheme: {
        light: {
          today: {
            background: 'var(--color-bg-element)',
            color: 'var(--color-text-primary)',
          },
          dropdown: {
            background: 'transparent',
            hoverBackground: 'var(--color-button-ghost-hover)',
            activeBackground: 'var(--color-button-ghost-active)',
            color: 'var(--color-icon-muted)',
            hoverColor: 'var(--color-icon-muted)',
            activeColor: 'var(--color-icon-muted)',
          },
        },
      },
    },
    // MultiSelect has no section of its own; the design system draws it as the
    // same field with SelectOption rendered in its `checkbox` variant.
    multiselect: {
      css: `
        .p-multiselect-label {
          font-size: var(--font-size-sm);
          font-weight: var(--font-weight-regular);
          line-height: var(--line-height-xs);
        }

        .p-multiselect-dropdown {
          border-inline-start: 1px solid var(--color-border-idle);
          border-start-end-radius: var(--radius-12);
          border-end-end-radius: var(--radius-12);
        }
      `,
      root: FORM_FIELD_SURFACE,
      dropdown: SELECT_DROPDOWN,
      overlay: SELECT_OVERLAY,
      // Header inset must equal list padding + option padding so a toggle-all
      // checkbox lines up with the option rows below it.
      list: {
        ...SELECT_LIST,
        header: {
          padding: 'var(--spacing-s) calc(var(--spacing-xs) + var(--spacing-s)) var(--spacing-xs)',
        },
      },
      option: SELECT_OPTION,
      chip: {
        borderRadius: 'var(--radius-16)',
      },
    },
  },
});

export default createPrimeVuePlugin();
